import * as THREE from 'three';
import type { SurfaceTextures } from './surfaceTextures';
import type { TileTraffic } from '../gen/traffic/network';
import { decalMaterial } from './streetAssets';
import { TERRAIN, TILE_SIZE } from '../gen/config';
import type { TileRoads } from '../gen/roads/generate';
import type { AnalysisResult, BlockInfo, BuildingInfo, CityResult, InstanceKind, MeshPayload, TerrainResult } from '../worker/protocol';
import { CityAssets } from './cityAssets';
import type { Shadows } from './shadows';
import { genToThree } from './coords';
import { CancelledError, type WorkerPool } from './workerPool';

export interface TileState {
  tx: number;
  ty: number;
  key: string;
  ring: number;
  goalLod: number;
  shownLod: number;
  requestedLod: number;
  terrain: THREE.Mesh | null;
  analysis: AnalysisResult | null;
  roads: TileRoads | null;
  blocks: BlockInfo[] | null;
  buildings: BuildingInfo[] | null;
  city: THREE.Group | null;
  /** Lanes, walking paths and signals (near tiles), for traffic and pedestrians. */
  traffic: TileTraffic | null;
  /** Detail requested for the city layer: 0 none, 1 shells, 2 shells + instanced details, 3 imposters. */
  cityLevel: number;
  /** What the city job was asked to build: 'full' (shells with instances, shown at levels 1 and 2) or 'imposter'. */
  cityBuild: 'full' | 'imposter' | null;
  minZ: number;
  maxZ: number;
  group: THREE.Group;
}

/** Work finished by a worker, waiting for its turn to touch the scene. */
interface ApplyItem {
  priority: number;
  apply: () => void;
}

export interface TileManagerOptions {
  /** Radius in tiles (circle test dx²+dy² < r², P/WorldManager.cpp:87). */
  viewRadius: number;
  /** Max milliseconds per frame spent applying finished work to the scene. */
  applyBudgetMs: number;
  /** Rings (Chebyshev) that get roads and city content. */
  cityRadius: number;
  /** Rings that also get instanced windows, doors, lamps and trees. */
  nearRadius: number;
  /** Rings beyond cityRadius that get simplified building imposters. */
  imposterRadius: number;
}

export function tileKey(tx: number, ty: number): string {
  return `${tx},${ty}`;
}

/** LOD by square ring, following AWorldManager: ring 0 → 5, 1 → 4, 2 → 3, 3 → 2, ≥ 4 → 1. */
export function lodForRing(ring: number): number {
  return Math.max(1, TERRAIN.maxLod - ring);
}

/**
 * Keeps tiles in rings around the camera, assigns LOD by ring, requests meshes from workers and
 * applies finished results under a per-frame time budget. Hard LOD swaps (no blending), like the
 * reference with its fade disabled.
 */
export class TileManager {
  readonly tiles = new Map<string, TileState>();
  readonly root = new THREE.Group();
  private readonly applyQueue: ApplyItem[] = [];
  /** Cascaded sun shadows (set by the app); lit materials register with it. */
  shadows: Shadows | null = null;
  /** Bumped whenever finished work touches tile data (for consumers that cache drawings of it). */
  dataVersion = 0;
  private centerTx = Number.NaN;
  private centerTy = Number.NaN;
  private readonly terrainMaterial: THREE.MeshStandardMaterial;
  readonly roadMaterial: THREE.MeshStandardMaterial;
  readonly assets = new CityAssets();
  readonly decalMaterial = decalMaterial();
  readonly groundMaterial = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.92, polygonOffset: true, polygonOffsetFactor: -1, polygonOffsetUnits: -2 });
  onTileAdded: ((t: TileState) => void) | null = null;
  onTileRemoved: ((t: TileState) => void) | null = null;
  onCityApplied: ((t: TileState, r: CityResult) => void) | null = null;

  constructor(
    private readonly pool: WorkerPool,
    private seed: number,
    readonly opts: TileManagerOptions,
  ) {
    this.root.name = 'tiles';
    this.terrainMaterial = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 1, metalness: 0 });
    this.roadMaterial = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.85, metalness: 0, polygonOffset: true, polygonOffsetFactor: -2, polygonOffsetUnits: -4 });
  }

  /** Register every shared lit material with the shadow system. */
  /** Texture the tile surfaces (before registerMaterials: the shadow hook chains onto this one). */
  applySurfaces(st: SurfaceTextures): void {
    st.patch(this.terrainMaterial, { terrain: true });
    for (const m of [this.roadMaterial, this.groundMaterial, this.assets.solid]) st.patch(m);
  }

  registerMaterials(s: Shadows): void {
    this.shadows = s;
    for (const m of [this.terrainMaterial, this.roadMaterial, this.groundMaterial, this.decalMaterial, this.assets.solid, this.assets.facade, ...Object.values(this.assets.materials)]) s.setup(m);
  }

  get applyQueueLength(): number {
    return this.applyQueue.length;
  }

  get cameraTile(): { tx: number; ty: number } {
    return { tx: this.centerTx, ty: this.centerTy };
  }

  setSeed(seed: number): void {
    this.seed = seed;
    this.clear();
  }

  clear(): void {
    this.pool.cancelWhere(() => true);
    for (const t of this.tiles.values()) this.disposeTile(t);
    this.tiles.clear();
    this.applyQueue.length = 0;
    this.centerTx = Number.NaN;
    this.centerTy = Number.NaN;
  }

  /** Called every frame with the camera position in generation space (cm). */
  update(camX: number, camY: number): void {
    const tx = Math.round(camX / TILE_SIZE);
    const ty = Math.round(camY / TILE_SIZE);
    if (tx !== this.centerTx || ty !== this.centerTy) {
      this.centerTx = tx;
      this.centerTy = ty;
      this.replan();
    }
    this.drainApplyQueue();
    if (this.assets.lod.size) for (const t of this.tiles.values()) if (t.city && t.cityLevel === 2) this.applyLod(t, camX, camY);
  }

  /**
   * Per-instance level of detail for kinds with models (cars, trees): instances within LOD_NEAR of the
   * camera are drawn with the model, the rest with the simple geometry, each set compacted into its own
   * instanced mesh (hidden instances would still cost vertex work). The original mesh keeps every
   * instance, hidden, as the collision proxy. Redone when the camera has moved 4 m.
   */
  private applyLod(t: TileState, x: number, y: number): void {
    const g = t.city!;
    const last = g.userData.lodAt as { x: number; y: number } | undefined;
    if (last && Math.hypot(last.x - x, last.y - y) < 400) return;
    g.userData.lodAt = { x, y };
    const h = TILE_SIZE / 2;
    const dx = Math.max(0, Math.abs(x - t.tx * TILE_SIZE) - h);
    const dy = Math.max(0, Math.abs(y - t.ty * TILE_SIZE) - h);
    const tileDist = Math.hypot(dx, dy);
    // Instance positions are relative to the tile origin, in metres (render space).
    const lx = (x - t.tx * TILE_SIZE) / 100;
    const lz = -(y - t.ty * TILE_SIZE) / 100;
    for (const o of [...g.children]) {
      const kind = o.name as InstanceKind;
      const lod = this.assets.lod.get(kind);
      if (!lod || !(o as THREE.InstancedMesh).isInstancedMesh || o.userData.lodPart) continue;
      const src = o as THREE.InstancedMesh;
      const range = (LOD_NEAR[kind] ?? LOD_NEAR.default) / 100;
      let parts = src.userData.lodParts as [THREE.InstancedMesh, THREE.InstancedMesh] | undefined;
      if (!parts) {
        const make = (lv: { geometry: THREE.BufferGeometry; material: THREE.Material | THREE.Material[] }, tag: string) => {
          const m = new THREE.InstancedMesh(lv.geometry, lv.material, src.count);
          m.name = `${kind}:${tag}`;
          m.userData.lodPart = true;
          m.userData.sharedGeometry = true;
          m.castShadow = src.castShadow;
          m.receiveShadow = src.receiveShadow;
          if (src.instanceColor) m.instanceColor = new THREE.InstancedBufferAttribute(new Float32Array(src.count * 3), 3);
          g.add(m);
          return m;
        };
        parts = [make(lod.high, 'hi'), make(lod.low, 'lo')];
        src.userData.lodParts = parts;
        src.userData.collider = true;
        src.visible = false;
      }
      const [hi, lo] = parts;
      const mat = src.instanceMatrix.array as Float32Array;
      const col = src.instanceColor?.array as Float32Array | undefined;
      let nh = 0;
      let nl = 0;
      for (let i = 0; i < src.count; i++) {
        const near = tileDist < range && Math.hypot(mat[i * 16 + 12] - lx, mat[i * 16 + 14] - lz) < range;
        const dst = near ? hi : lo;
        const j = near ? nh++ : nl++;
        (dst.instanceMatrix.array as Float32Array).set(mat.subarray(i * 16, i * 16 + 16), j * 16);
        if (col && dst.instanceColor) (dst.instanceColor.array as Float32Array).set(col.subarray(i * 3, i * 3 + 3), j * 3);
      }
      hi.count = nh;
      lo.count = nl;
      for (const m of parts) {
        m.visible = m.count > 0;
        m.instanceMatrix.needsUpdate = true;
        if (m.instanceColor) m.instanceColor.needsUpdate = true;
        m.computeBoundingSphere();
      }
    }
  }


  private replan(): void {
    const R = this.opts.viewRadius;
    const wanted = new Set<string>();
    for (let dy = -R; dy <= R; dy++) {
      for (let dx = -R; dx <= R; dx++) {
        if (dx * dx + dy * dy >= R * R && !(dx === 0 && dy === 0)) continue;
        const tx = this.centerTx + dx;
        const ty = this.centerTy + dy;
        const key = tileKey(tx, ty);
        wanted.add(key);
        const ring = Math.max(Math.abs(dx), Math.abs(dy));
        let t = this.tiles.get(key);
        if (!t) {
          t = this.createTile(tx, ty);
          this.tiles.set(key, t);
          this.onTileAdded?.(t);
        }
        t.ring = ring;
        const lod = lodForRing(ring);
        t.goalLod = lod;
        if (lod !== t.shownLod && lod !== t.requestedLod) this.requestTerrain(t, lod);
        const level = ring <= this.opts.nearRadius ? 2 : ring <= this.opts.cityRadius ? 1 : ring <= this.opts.imposterRadius ? 3 : 0;
        if (level > 0 && level !== t.cityLevel) this.requestCity(t, level);
      }
    }
    // Unload with one ring of hysteresis (the reference never unloads, SPEC §2.1).
    const keep = (R + 1) * (R + 1);
    for (const [key, t] of this.tiles) {
      if (wanted.has(key)) continue;
      const dx = t.tx - this.centerTx;
      const dy = t.ty - this.centerTy;
      if (dx * dx + dy * dy >= keep) {
        this.pool.cancelWhere((k) => k.startsWith(`t:${key}:`));
        this.disposeTile(t);
        this.tiles.delete(key);
        this.onTileRemoved?.(t);
      }
    }
    this.pool.reprioritize((k, old) => {
      if (!k?.startsWith('t:')) return old;
      const [xs, ys] = k.slice(2).split(':')[0].split(',');
      return Math.hypot(Number(xs) - this.centerTx, Number(ys) - this.centerTy);
    });
  }

  private createTile(tx: number, ty: number): TileState {
    const group = new THREE.Group();
    group.name = `tile ${tx},${ty}`;
    genToThree(tx * TILE_SIZE, ty * TILE_SIZE, 0, group.position);
    this.root.add(group);
    const t: TileState = {
      tx,
      ty,
      key: tileKey(tx, ty),
      ring: 0,
      goalLod: 0,
      shownLod: 0,
      requestedLod: 0,
      terrain: null,
      analysis: null,
      roads: null,
      blocks: null,
      buildings: null,
      city: null,
      traffic: null,
      cityLevel: 0,
      cityBuild: null,
      minZ: 0,
      maxZ: 0,
      group,
    };
    void this.pool
      .submit('analysis', this.seed, { tx, ty }, this.priorityOf(t), `t:${t.key}:analysis`)
      .then((a) => this.enqueue(this.priorityOf(t), () => (t.analysis = a)))
      .catch(ignoreCancel);
    return t;
  }

  priorityOf(t: TileState): number {
    return Math.hypot(t.tx - this.centerTx, t.ty - this.centerTy);
  }

  private requestTerrain(t: TileState, lod: number): void {
    t.requestedLod = lod;
    const key = `t:${t.key}:terrain`;
    this.pool.cancel(key);
    void this.pool
      .submit('terrain', this.seed, { tx: t.tx, ty: t.ty, lod, excavate: t.ring <= this.opts.cityRadius }, this.priorityOf(t), key)
      .then((r) => this.enqueue(this.priorityOf(t), () => this.applyTerrain(t, r)))
      .catch(ignoreCancel);
  }

  /**
   * Shells and near detail share one build: the city job always includes the instance buffers
   * (≈ 0.2 MB a tile, no extra job time), so moving between levels 1 and 2 only toggles the instanced
   * meshes' visibility instead of regenerating the tile.
   */
  private requestCity(t: TileState, level: number): void {
    t.cityLevel = level;
    const build = level === 3 ? 'imposter' : 'full';
    if (t.cityBuild === build) {
      if (t.city) this.showInstances(t);
      return;
    }
    t.cityBuild = build;
    const key = `t:${t.key}:city`;
    this.pool.cancel(key);
    void this.pool
      .submit('city', this.seed, { tx: t.tx, ty: t.ty, near: build === 'full', imposter: build === 'imposter' }, this.priorityOf(t) + (level === 3 ? 2 : 0.5), key)
      .then((r) => this.enqueue(this.priorityOf(t) + 0.5, () => this.applyCity(t, r)))
      .catch(ignoreCancel);
  }

  private applyCity(t: TileState, r: CityResult): void {
    if (this.tiles.get(t.key) !== t) return;
    t.roads = r.roads;
    t.blocks = r.blocks;
    t.buildings = r.buildings;
    t.traffic = r.traffic ?? null;
    if (t.city) {
      t.group.remove(t.city);
      disposeGroup(t.city);
    }
    const g = new THREE.Group();
    g.name = 'city';
    if (r.meshes.roads.indices.length > 0) g.add(named(new THREE.Mesh(makeGeometry(r.meshes.roads), this.roadMaterial), 'roads'));
    if (r.meshes.solid.indices.length > 0) g.add(named(new THREE.Mesh(makeGeometry(r.meshes.solid), this.assets.solid), 'solid'));
    if (r.meshes.ground.indices.length > 0) g.add(named(new THREE.Mesh(makeGeometry(r.meshes.ground), this.groundMaterial), 'ground'));
    if (r.meshes.facade.indices.length > 0) g.add(named(new THREE.Mesh(makeGeometry(r.meshes.facade), this.assets.facade), 'facade'));
    if (r.meshes.decals && r.meshes.decals.indices.length > 0) g.add(named(new THREE.Mesh(makeGeometry(r.meshes.decals), this.decalMaterial), 'decals'));
    for (const k of Object.keys(r.instances) as InstanceKind[]) {
      const m = this.assets.instanced(k, r.instances[k], r.instanceData?.[k]);
      if (m) g.add(m);
    }
    // Shadows: near city content casts (not imposters, roads or draped ground); everything receives.
    const cast = t.cityBuild === 'full';
    for (const o of g.children) {
      o.receiveShadow = true;
      o.castShadow = cast && !NO_SHADOW.has(o.name);
    }
    t.city = g;
    t.group.add(g);
    this.showInstances(t);
    this.onCityApplied?.(t, r);
  }

  /**
   * Show generated interiors through tower windows: for each building id in `cuts`, the glass of its
   * facade between world heights [yMin, yMax] (m) is discarded and its window panes there are hidden.
   * Re-applied automatically when the tile's city group is rebuilt (the signature lives on the group).
   */
  setCutouts(t: TileState, cuts: Map<string, [number, number]>): void {
    const g = t.city;
    if (!g || !t.buildings) return;
    const sig = [...cuts].map(([id, r]) => `${id}:${r[0].toFixed(2)}:${r[1].toFixed(2)}`).sort().join('|');
    if ((g.userData.cutSig ?? '') === sig) return;
    g.userData.cutSig = sig;
    const facade = g.getObjectByName('facade') as THREE.Mesh | undefined;
    const windows = g.getObjectByName('towerWindow') as THREE.InstancedMesh | undefined;
    if (facade) {
      const geo = facade.geometry;
      for (const m of Array.isArray(facade.material) ? facade.material : []) if (m.userData.owned) m.dispose();
      geo.clearGroups();
      facade.material = this.assets.facade;
      const ranges = t.buildings.filter((b) => b.facadeRange && b.facadeRange[1] > 0 && cuts.has(b.id)).sort((a, b) => a.facadeRange![0] - b.facadeRange![0]);
      if (ranges.length > 0) {
        const mats: THREE.Material[] = [this.assets.facade];
        let at = 0;
        const total = geo.index!.count;
        for (const b of ranges) {
          const [s, n] = b.facadeRange!;
          if (s > at) geo.addGroup(at, s - at, 0);
          const [y0, y1] = cuts.get(b.id)!;
          geo.addGroup(s, n, mats.length);
          const cut = this.assets.glassCutout(y0, y1);
          this.shadows?.setup(cut);
          mats.push(cut);
          at = s + n;
        }
        if (at < total) geo.addGroup(at, total - at, 0);
        facade.material = mats;
      }
    }
    // Glass panes over the cut floors are hidden: tower windows by building, low-rise ones by unit.
    const hide = (mesh: THREE.InstancedMesh | undefined, ranges: { id: string; range?: [number, number] }[]) => {
      if (!mesh) return;
      const attr = mesh.instanceMatrix;
      const orig: Float32Array = mesh.userData.orig ?? (mesh.userData.orig = (attr.array as Float32Array).slice());
      const arr = attr.array as Float32Array;
      arr.set(orig);
      for (const { id, range: r } of ranges) {
        const c = cuts.get(id);
        if (!r || !c) continue;
        for (let i = r[0]; i < r[0] + r[1]; i++) {
          const y = orig[i * 16 + 13];
          if (y > c[0] && y < c[1]) arr.fill(0, i * 16, i * 16 + 15);
        }
      }
      attr.needsUpdate = true;
    };
    hide(windows, t.buildings.map((b) => ({ id: b.id, range: b.windowRange })));
    hide(
      g.getObjectByName('lowWindow') as THREE.InstancedMesh | undefined,
      t.buildings.flatMap((b) => (b.units ?? []).map((u) => ({ id: u.id, range: u.windowRange }))),
    );
  }

  private showInstances(t: TileState): void {
    // LOD sources stay hidden (their compacted copies draw them).
    for (const o of t.city?.children ?? []) if ((o as THREE.InstancedMesh).isInstancedMesh) o.visible = t.cityLevel === 2 && !o.userData.lodParts;
  }

  enqueue(priority: number, apply: () => void): void {
    this.applyQueue.push({ priority, apply });
  }

  private applyTerrain(t: TileState, r: TerrainResult): void {
    if (this.tiles.get(t.key) !== t) return; // unloaded meanwhile
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(r.mesh.positions, 3));
    geo.setAttribute('normal', new THREE.BufferAttribute(r.mesh.normals, 3));
    if (r.mesh.colors) geo.setAttribute('color', new THREE.BufferAttribute(r.mesh.colors, 3));
    geo.setIndex(new THREE.BufferAttribute(r.mesh.indices, 1));
    geo.computeBoundingSphere();
    const mesh = new THREE.Mesh(geo, this.terrainMaterial);
    mesh.name = `terrain lod${r.lod}`;
    mesh.receiveShadow = true;
    // Hard swap.
    if (t.terrain) {
      t.group.remove(t.terrain);
      t.terrain.geometry.dispose();
    }
    t.terrain = mesh;
    t.group.add(mesh);
    t.shownLod = r.lod;
    t.minZ = r.minZ;
    t.maxZ = r.maxZ;
  }

  private drainApplyQueue(): void {
    if (this.applyQueue.length === 0) return;
    this.applyQueue.sort((a, b) => a.priority - b.priority);
    const t0 = performance.now();
    let n = 0;
    while (this.applyQueue.length > 0) {
      const item = this.applyQueue.shift()!;
      item.apply();
      this.dataVersion++;
      n++;
      if (performance.now() - t0 > this.opts.applyBudgetMs && n >= 1) break;
    }
  }

  private disposeTile(t: TileState): void {
    disposeGroup(t.group);
    this.root.remove(t.group);
  }
}

/** Distance (cm) from the camera within which cars and trees use the models. */
const LOD_NEAR: Partial<Record<InstanceKind, number>> & { default: number } = { default: 9000, tree0: 7000, tree1: 7000, tree2: 7000 };

/**
 * City meshes that don't cast sun shadows: flat on the ground or on walls (window panes, doors, sills,
 * signs, decals), where a shadow is invisible but still costs a draw per cascade.
 */
const NO_SHADOW = new Set(['roads', 'ground', 'decals', 'towerWindow', 'lowWindow', 'door', 'windowTrim', 'sign']);

function named<T extends THREE.Object3D>(o: T, name: string): T {
  o.name = name;
  return o;
}

export function disposeGroup(g: THREE.Object3D): void {
  g.traverse((o) => {
    const m = o as THREE.Mesh;
    // A batched mesh also owns its matrix and colour textures.
    if (o instanceof THREE.BatchedMesh || o instanceof THREE.InstancedMesh) o.dispose();
    else if (m.geometry && !m.userData.sharedGeometry) m.geometry.dispose();
    if (Array.isArray(m.material)) for (const mat of m.material) if (mat.userData.owned) mat.dispose();
  });
}

export function makeGeometry(m: MeshPayload): THREE.BufferGeometry {
  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.BufferAttribute(m.positions, 3));
  geo.setAttribute('normal', new THREE.BufferAttribute(m.normals, 3));
  if (m.colors) geo.setAttribute('color', new THREE.BufferAttribute(m.colors, 3));
  if (m.uvs) geo.setAttribute('uv', new THREE.BufferAttribute(m.uvs, 2));
  if (m.surfaces) geo.setAttribute('surface', new THREE.BufferAttribute(m.surfaces, 1));
  geo.setIndex(new THREE.BufferAttribute(m.indices, 1));
  geo.computeBoundingSphere();
  return geo;
}

export function ignoreCancel(e: unknown): void {
  if (e instanceof CancelledError) return;
  console.error(e);
}
